import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'

import dayjs from 'dayjs'

import BarAndLineChart from '@/components/view/atoms/chart/BarAndLineChart'
import ChartWrapper from '@/components/view/layouts/chart/ChartWrapper'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import { fetchRest } from '@/common/api/fetcher'

// 共通スタイル（SmartHub デザイントークン）
const CARD = 'rounded-xl border border-secondary bg-surface shadow-area'
const INPUT = 'h-8 rounded-lg border border-border-input bg-surface px-2.5 text-size-13 text-primary outline-none hover:border-border-input-hover focus:border-border-input-active placeholder:text-placeholder'

const TH = 'text-secondary border-b border-secondary px-2.5 py-2 text-left text-[11px] font-semibold whitespace-nowrap'
const TD = 'text-primary px-2.5 py-2 align-middle'

/** CVR比較（backend/routers/ugc_analytics.py の get_cvr_comparison） */
interface CvrSide {
  sessions: number
  purchases: number
  cvr: number
  views: number
}

interface CvrComparison {
  period_days: number
  basis: 'session' | 'visitor'
  with_ugc: CvrSide
  without_ugc: CvrSide
  improvement: number | null
  improvement_pct: number | null
  excluded_logs: number
}

/** 日別トレンド（同 get_trend） */
interface TrendPoint {
  date: string
  views: number
  clicks: number
  purchases: number
  cvr: number
}

/** 貢献UGC（同 get_top_ugc） */
interface TopUgcRow {
  ugc_item_id: number
  media_url: string | null
  source_type: string | null
  author_handle: string | null
  ai_score: number | null
  impressions: number
  clicks: number
  purchases: number
  revenue: number
  ctr: number
}

/** SKU×流入経路（同 get_sku_matrix） */
interface MatrixRow {
  product_id: string | null
  utm_source: string
  views: number
  clicks: number
  purchases: number
  revenue: number
  ctr: number
  cvr: number
}

/** レビュー枠の操作（同 get_ui_action_impact） */
interface UiActionRow {
  action: string
  label: string
  events: number
  sessions: number
  purchases: number
  cvr: number
}

interface UiActionImpact {
  period_days: number
  basis: 'session' | 'visitor'
  rows: UiActionRow[]
  engaged: CvrSide
  not_engaged: CvrSide
  improvement: number | null
  improvement_pct: number | null
}

export default function UGCCVRDashboard() {
  const { siteId } = useParams()
  const [cvr, setCvr] = useState<CvrComparison | null>(null)
  const [trend, setTrend] = useState<{ trend?: TrendPoint[] } | null>(null)
  const [topUgc, setTopUgc] = useState<{ top_ugc?: TopUgcRow[] } | null>(null)
  const [matrix, setMatrix] = useState<{ matrix?: MatrixRow[] } | null>(null)
  const [uiActions, setUiActions] = useState<UiActionImpact | null>(null)
  const [days, setDays] = useState(30)
  const [loading, setLoading] = useState(true)
  // 比較の単位。session_id が入っていないログしか無い環境ではサーバが訪問者単位に切り替える
  const unitLabel = cvr?.basis === 'visitor' ? '訪問者' : 'セッション'
  // JSX の中で毎回 null 判定を書かずに済むよう、ここで既定値へ寄せる
  const trendPoints = trend?.trend ?? []
  const topUgcRows = topUgc?.top_ugc ?? []
  const matrixRows = matrix?.matrix ?? []
  const uiRows = uiActions?.rows ?? []
  const excludedLogs = cvr?.excluded_logs ?? 0

  useEffect(() => {
    setLoading(true)
    Promise.all([
      fetchRest<CvrComparison>(`/api/sites/${siteId}/ugc/analytics/cvr?days=${days}`),
      fetchRest<{ trend?: TrendPoint[] }>(
        `/api/sites/${siteId}/ugc/analytics/trend?days=${Math.min(days, 30)}`
      ),
      fetchRest<{ top_ugc?: TopUgcRow[] }>(
        `/api/sites/${siteId}/ugc/analytics/top-ugc?days=${days}&limit=5`
      ),
      fetchRest<{ matrix?: MatrixRow[] }>(
        `/api/sites/${siteId}/ugc/analytics/sku-matrix?days=${days}&limit=10`
      ),
      fetchRest<UiActionImpact>(`/api/sites/${siteId}/ugc/analytics/ui-actions?days=${days}`),
    ])
      .then(([c, t, u, m, a]) => { setCvr(c); setTrend(t); setTopUgc(u); setMatrix(m); setUiActions(a) })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [siteId, days])

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='📈 CVRダッシュボード'
        description='UGCを見た人と見ていない人のCVRを比較し、日別トレンドと貢献UGC/SKUを表示します'
        rightNode={
          <select value={days} onChange={e => setDays(Number(e.target.value))} className={INPUT}>
            <option value={7}>7日間</option>
            <option value={30}>30日間</option>
            <option value={90}>90日間</option>
          </select>
        }
      />

      {loading ? (
        <div className='text-secondary py-16 text-center text-sm'>読み込み中...</div>
      ) : (
        <>
          {/* CVR比較（セッション単位。session_idが無い環境では訪問者単位） */}
          <div className='grid grid-cols-1 gap-4 md:grid-cols-3'>
            <div className={`${CARD} px-4 py-3.5 text-center`}>
              <div className='text-secondary text-xs'>UGCを見た{unitLabel} CVR</div>
              <div className='text-success text-2xl font-bold'>{cvr?.with_ugc?.cvr ?? 0}%</div>
              <div className='text-tertiary text-[10px]'>
                {(cvr?.with_ugc?.sessions ?? 0).toLocaleString()} {unitLabel} → {cvr?.with_ugc?.purchases ?? 0} 件購入
              </div>
            </div>
            <div className={`${CARD} px-4 py-3.5 text-center`}>
              <div className='text-secondary text-xs'>見ていない{unitLabel} CVR</div>
              <div className='text-critical text-2xl font-bold'>{cvr?.without_ugc?.cvr ?? 0}%</div>
              <div className='text-tertiary text-[10px]'>
                {(cvr?.without_ugc?.sessions ?? 0).toLocaleString()} {unitLabel} → {cvr?.without_ugc?.purchases ?? 0} 件購入
              </div>
            </div>
            <div className={`${CARD} px-4 py-3.5 text-center`}>
              <div className='text-secondary text-xs'>改善率</div>
              {/* 母数が片側0のときサーバは null を返す。0% と出すと「差が無い」に見えてしまう */}
              {cvr?.improvement_pct == null ? (
                <>
                  <div className='text-tertiary text-2xl font-bold'>—</div>
                  <div className='text-tertiary text-[10px]'>
                    {(cvr?.without_ugc?.sessions ?? 0) === 0
                      ? `UGCを見ていない${unitLabel}が居ないため比較できません`
                      : '比較できるデータが足りません'}
                  </div>
                </>
              ) : (
                <>
                  <div className={`text-2xl font-bold ${cvr.improvement_pct > 0 ? 'text-success' : 'text-tertiary'}`}>
                    {cvr.improvement_pct > 0 ? '+' : ''}{cvr.improvement_pct}%
                  </div>
                  <div className='text-tertiary text-[10px]'>
                    CVR差分: {(cvr.improvement ?? 0) > 0 ? '+' : ''}{cvr.improvement ?? 0}pt
                  </div>
                </>
              )}
            </div>
          </div>

          {/* 計測できていないログがあると母数が合わない。理由が分かるように出す */}
          {excludedLogs > 0 && (
            <p className='text-tertiary mt-2 mb-0 text-[10px]'>
              セッションIDと訪問者IDのどちらも無いログ {excludedLogs.toLocaleString()} 件は、
              どちらの{unitLabel}にも数えていません。
            </p>
          )}

          {/* レビュー枠のUI操作。並び替え・絞り込み・もっと見るがCVRに効いたかを見る */}
          <div className={`${CARD} mt-4 p-5`}>
            <h3 className='text-primary mb-1 text-sm font-bold'>レビュー枠の操作</h3>
            <p className='text-tertiary mt-0 mb-3 text-[10px]'>
              1{unitLabel}で何回操作しても1と数えます（回数で割ると、よく触った人ほどCVRが低く見えるため）。
              比較の母数はUGCを見た{unitLabel}に限っています。
            </p>

            {uiActions && uiRows.some(r => r.events > 0) ? (
              <>
                <div className='overflow-x-auto'>
                  <table className='w-full border-collapse text-xs'>
                    <thead>
                      <tr>
                        <th className={TH}>操作</th>
                        <th className={`${TH} text-right`}>回数</th>
                        <th className={`${TH} text-right`}>{unitLabel}数</th>
                        <th className={`${TH} text-right`}>購入</th>
                        <th className={`${TH} text-right`}>CVR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uiRows.map(r => (
                        <tr key={r.action} className='border-b border-secondary'>
                          <td className={`${TD} font-semibold`}>{r.label}</td>
                          <td className={`${TD} text-right`}>{r.events.toLocaleString()}</td>
                          <td className={`${TD} text-right`}>{r.sessions.toLocaleString()}</td>
                          <td className={`${TD} text-right`}>{r.purchases.toLocaleString()}</td>
                          <td className={`${TD} text-right`}>{r.cvr}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className='mt-4 grid grid-cols-1 gap-4 md:grid-cols-3'>
                  <div className='bg-surface-secondary rounded-lg px-4 py-3 text-center'>
                    <div className='text-secondary text-xs'>操作した{unitLabel} CVR</div>
                    <div className='text-success text-xl font-bold'>{uiActions.engaged.cvr}%</div>
                    <div className='text-tertiary text-[10px]'>
                      {uiActions.engaged.sessions.toLocaleString()} {unitLabel} → {uiActions.engaged.purchases} 件購入
                    </div>
                  </div>
                  <div className='bg-surface-secondary rounded-lg px-4 py-3 text-center'>
                    <div className='text-secondary text-xs'>操作しなかった{unitLabel} CVR</div>
                    <div className='text-primary text-xl font-bold'>{uiActions.not_engaged.cvr}%</div>
                    <div className='text-tertiary text-[10px]'>
                      {uiActions.not_engaged.sessions.toLocaleString()} {unitLabel} → {uiActions.not_engaged.purchases} 件購入
                    </div>
                  </div>
                  <div className='bg-surface-secondary rounded-lg px-4 py-3 text-center'>
                    <div className='text-secondary text-xs'>差</div>
                    {/* 片側の母数が0のときサーバは null を返す。0% と出すと「差が無い」に見えてしまう */}
                    {uiActions.improvement_pct == null ? (
                      <>
                        <div className='text-tertiary text-xl font-bold'>—</div>
                        <div className='text-tertiary text-[10px]'>比較できるデータが足りません</div>
                      </>
                    ) : (
                      <>
                        <div className={`text-xl font-bold ${uiActions.improvement_pct > 0 ? 'text-success' : 'text-tertiary'}`}>
                          {uiActions.improvement_pct > 0 ? '+' : ''}{uiActions.improvement_pct}%
                        </div>
                        <div className='text-tertiary text-[10px]'>
                          CVR差分: {(uiActions.improvement ?? 0) > 0 ? '+' : ''}{uiActions.improvement ?? 0}pt
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <p className='text-tertiary text-xs'>
                操作の記録がありません。レビュー枠の並び替え・絞り込みを出す設定になっているかご確認ください。
              </p>
            )}
          </div>

          {/* トレンド */}
          <div className={`${CARD} mt-4 p-5`}>
            <h3 className='text-primary mb-3 text-sm font-bold'>日別トレンド</h3>
            {/* 2026-09-29 修正：div の棒は親の高さが決まらず高さ0で潰れていたため、共通の Chart.js 部品に置き換え */}
            {trendPoints.length > 0 ? (
              <ChartWrapper>
                <BarAndLineChart
                  labels={trendPoints.map((d) => dayjs(d.date).format('M/D'))}
                  barItems={[
                    { title: '閲覧数', values: trendPoints.map((d) => d.views) },
                    { title: '購入数', values: trendPoints.map((d) => d.purchases) }
                  ]}
                  lineItems={[{ title: 'CVR', values: trendPoints.map((d) => d.cvr) }]}
                />
              </ChartWrapper>
            ) : <p className='text-tertiary text-xs'>データなし</p>}
          </div>

          <div className='mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2'>
            {/* トップUGC */}
            <div className={`${CARD} p-5`}>
              <h3 className='text-primary mb-3 text-sm font-bold'>CVR貢献 Top UGC</h3>
              {topUgcRows.length > 0 ? (
                <div className='flex flex-col gap-2'>
                  {topUgcRows.map((u, i) => (
                    <div key={u.ugc_item_id} className='bg-surface-secondary flex items-center gap-2.5 rounded-lg p-2'>
                      <span className='text-tertiary w-5 text-sm font-bold'>#{i + 1}</span>
                      {u.media_url && <img src={u.media_url} alt='' className='h-10 w-10 rounded-md object-cover' />}
                      <div className='flex-1'>
                        <div className='text-secondary text-[11px]'>{u.author_handle || `UGC #${u.ugc_item_id}`}</div>
                        <div className='text-tertiary text-[10px]'>{u.impressions} imp → {u.clicks} click → {u.purchases} CV</div>
                      </div>
                      <span className='text-success text-xs font-bold'>{u.ctr}%</span>
                    </div>
                  ))}
                </div>
              ) : <p className='text-tertiary text-xs'>データなし</p>}
            </div>

            {/* SKUマトリクス */}
            <div className={`${CARD} p-5`}>
              <h3 className='text-primary mb-3 text-sm font-bold'>SKU×流入経路</h3>
              {matrixRows.length > 0 ? (
                <div className='overflow-x-auto'>
                  <table className='w-full border-collapse text-[11px]'>
                    <thead>
                      <tr className='bg-surface-secondary'>
                        <th className={TH}>商品</th>
                        <th className={TH}>経路</th>
                        <th className={TH}>Views</th>
                        <th className={TH}>CVR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {matrixRows.map((r, i) => (
                        <tr key={i} className='border-b border-secondary'>
                          <td className={TD}>{r.product_id}</td>
                          <td className={TD}>{r.utm_source}</td>
                          <td className={TD}>{r.views}</td>
                          <td className={TD}>{r.cvr}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <p className='text-tertiary text-xs'>データなし</p>}
            </div>
          </div>
        </>
      )}
    </Main>
  )
}
